import { ReactNode } from 'react'
import { useSearchParams } from 'react-router'

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

type TabItem = {
  value: string
  label: ReactNode
  content: ReactNode
}

type PageTabsProps = {
  /** クエリパラメータ名 (デフォルト: "tab") */
  paramName?: string
  /** タブ定義 */
  tabs: TabItem[]
  /** デフォルトタブ値 (未指定時は最初のタブ) */
  defaultTab?: string
  /** Tabsコンテナに追加するクラス */
  className?: string
  /** TabsContentに追加するクラス */
  contentClassName?: string
  /** タブリストとコンテンツの間に表示する要素 (現在のタブ値を受け取るrender prop) */
  afterTabs?: ReactNode | ((activeTab: string) => ReactNode)
}

export function PageTabs({
  paramName = 'tab',
  tabs,
  defaultTab,
  className = 'h-full flex flex-col',
  contentClassName = 'flex-1 overflow-hidden',
  afterTabs,
}: PageTabsProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get(paramName) ?? defaultTab ?? tabs[0]?.value ?? ''

  const handleTabChange = (value: string) => {
    const defaultValue = defaultTab ?? tabs[0]?.value
    const next = new URLSearchParams(searchParams)
    if (value === defaultValue) {
      next.delete(paramName)
    } else {
      next.set(paramName, value)
    }
    setSearchParams(next)
  }

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange} className={className}>
      <div className="border-b bg-card">
        <TabsList className="w-full justify-start rounded-none h-auto p-0 bg-transparent overflow-x-auto scrollbar-none">
          {tabs.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              className="shrink-0 rounded-none data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-primary"
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {typeof afterTabs === 'function' ? afterTabs(activeTab) : afterTabs}
      </div>

      {tabs.map((tab) => (
        <TabsContent key={tab.value} value={tab.value} className={contentClassName}>
          {tab.content}
        </TabsContent>
      ))}
    </Tabs>
  )
}
